import { VinkReels } from '@schibsted-regional/components';
import React from 'react';

import type { IImpressionWidget } from '@schibsted/pulse-sdk';
import type { ApReelsProps } from '@schibsted-regional/components';
import type { SvpAsset } from '../../../../core/bindings/data/fetch-stream-data.js';
import type { PulseContextInputParams } from '../../../../core/mandatory-integrations/pulse/types/index.js';

import { isMobileApp } from '../../../../core/utils/is-mobile.js';
import { useReelsOverlay } from '../../../../regional/rendering/blocks/Reels/useReelsOverlay.js';
import { buildImpressionWidgetAttributes } from '../../../../core/mandatory-integrations/pulse/event/input-builders/impression/build-impression-widget-attributes.js';
import { usePulseContext } from '../../../../core/mandatory-integrations/pulse/PulseProvider.js';

type ReelsSliderProps = {
  title: string;
  playlistId: string;
  reels: SvpAsset[];
  url: string;
  primaryPosition: number;
};

const ReelsSlider = ({
  title,
  reels,
  playlistId,
  url,
  primaryPosition,
}: ReelsSliderProps) => {
  const { trackClickWidget } = usePulseContext();

  const tpaasTrackingInput: PulseContextInputParams<IImpressionWidget> = {
    object: {
      name: 'vink-reels-slider',
      objectId: `vink-reels-${playlistId}`,
    },
    placement: {
      columnName: 'main',
      containerName: 'page',
      foldPosition: 'Unknown',
      primaryAxis: 'Vertical',
      primaryPosition,
      secondaryPosition: 1,
    },
  };

  const preloadUrls = reels.map(
    ({ id }) => `${url}/stories/${id}?playlistId=${playlistId}`,
  );

  const previews = reels.map((video, index) => {
    const { metadata } = video.additional;

    return {
      id: video.id,
      title: video.title,
      link: `${url}/stories/${video.id}?playlistId=${playlistId}`,
      duration: Number(video.duration),
      imagePreviewUrl: video.images.main,
      videoPreviewUrl: `${
        metadata?.preview_vivi_category ||
        metadata?.preview_360p ||
        metadata?.preview ||
        metadata?.preview_hq
      }`,
      'data-pulse-entity-id': `reel-${video.id}`,
      'data-pulse-entity-name': `Reel ${video.id}: ${video.title}`,
      'data-pulse-position-in-bundle': `${index + 1}`,
      'data-pulse-source': 'reels-widget',
      'data-pulse-product-name': 'reels-widget',
    };
  });

  const { openReel } = useReelsOverlay({ preloadItems: preloadUrls });

  const onPreviewClick: ApReelsProps['onPreviewClick'] = (event, video) => {
    const index = reels.findIndex((reel) => reel.id === video.id);

    trackClickWidget(
      {
        object: {
          id: `reel-${video.id}`,
          name: `Reel video-${video.id}`,
          elementType: 'Teaser',
          placement: {
            primaryPosition: index + 1,
            positionInBundle: index + 1,
          },
        },
      },
      {
        ...tpaasTrackingInput,
        engagement: {
          elementName: `Reel ${video.id}`,
        },
      },
    );

    const useIframeOverlay = !isMobileApp();

    if (useIframeOverlay) {
      event.preventDefault();
      openReel(video.link);
    }
  };

  const commonProps: ApReelsProps = { title, previews, onPreviewClick };

  return (
    <div
      data-pulse-entity-id={`vink-reels-${playlistId}`}
      data-pulse-entity-name="vink reels sliders"
      data-pulse-entity-type="Reel"
      {...buildImpressionWidgetAttributes(tpaasTrackingInput)}
      className="reels-widget"
    >
      <VinkReels {...commonProps} />
    </div>
  );
};

export { ReelsSlider };
